/**
 * DEMO / TRIAL ONLY — synthetic regularization scenarios for the Campaigning Tool demo.
 *
 * This is NOT the production data-fetch. In production, opening a regularization link fetches the
 * vendor's real `original_data` from the backend (see vendor-regularization-form/index.jsx). These
 * two hard-coded scenarios exist purely to demonstrate the two shapes a regularization vendor can
 * take, driven by a `?demo=recent|legacy` query param. No real vendor data. No emails are sent.
 *
 * FOLLOW-UP (not built here): the real system needs logic to decide which scenario a given vendor
 * falls into — likely by onboarding date or a data-completeness check on the vendor record.
 *
 * `original_data` mirrors the exact shape convertRegularizationToFormData() consumes, so both demo
 * and production flow through the identical prefill path.
 */

// Scenario A — a recently-onboarded vendor: most details already on file, a few flagged to re-check.
const RECENT_VENDOR = {
  scenario: 'recent',
  label: 'Recent vendor — verifying existing details',
  // Fields the vendor should double-check (reads as "please confirm", not an error).
  flaggedFields: [
    { key: 'registeredState', message: 'This state value may be stale — please reselect it from the dropdown.' },
    { key: 'gstNumber', message: 'Please re-verify your GST number.' },
    { key: 'ifscCode', message: 'Please double-check your bank IFSC / account details.' },
  ],
  // NOTE: registry-verifiable numbers (GSTIN/PAN/Udyam/bank) are the mock's known test identity so
  // the LIVE SurePass lookups actually succeed and stay consistent. Contact/SPOC names remain
  // synthetic. The flagged-field framing (State/GST/Bank) is unchanged.
  original_data: {
    company_info: {
      company_name: 'Reliance Industries Limited',
      business_vertical: 'amber-enterprises',
      contact_person_name: 'Anita Verma',
      designation: 'Procurement Head',
      director_name: 'Rakesh Malhotra',
      director_email: 'rakesh.malhotra@novacomponents.example',
      alt_spoc_name: 'Sunil Rao',
      alt_spoc_email: 'sunil.rao@novacomponents.example',
      alt_spoc_designation: 'Accounts Manager',
      alt_spoc_phone: '9820044556',
      email: 'anita.verma@novacomponents.example',
      phone_number: '9820011234',
      website: 'https://novacomponents.example',
      year_established: 2015,
      business_description: 'Precision sheet-metal components for consumer durables.',
    },
    addresses: {
      registered_address: 'Plot 42, MIDC Industrial Area',
      registered_city: 'Pune',
      // Intentionally a non-canonical spelling so it reads as "needs reselection" in the demo.
      registered_state: 'Maharastra',
      registered_district: 'Pune',
      registered_country: 'IN',
      registered_pincode: '411001',
      supply_address: 'Plot 42, MIDC Industrial Area',
      supply_city: 'Pune',
      supply_state: 'Maharastra',
      supply_district: 'Pune',
      supply_country: 'IN',
      supply_pincode: '411001',
    },
    bank_details: {
      bank_name: 'HDFC Bank',
      branch_name: 'Fort, Mumbai',
      account_type: 'current',
      ifsc_code: 'HDFC0000001', // mock-verifiable IFSC
      swift_code: '',
      bank_address: 'Manekji Wadia Building, Fort, Mumbai 400001',
    },
    msme: {
      msme_status: 'registered',
      msme_category: 'medium',
      msme_number: 'UDYAM-MH-18-0000001', // mock-known Udyam number
      classification_year: 2023,
      classification_date: '2023-05-09', // matches the Udyam-cert OCR mock
    },
    compliance: {
      pan_number: 'AAACR5055K', // mock-known PAN (matches PAN OCR mock)
      gst_number: '27AAACR5055K1Z7', // mock-known GSTIN (matches GST OCR mock + GSTIN lookup)
      gta_registration: '',
      nature_of_assessee: 'company',
    },
    classification: { vendor_type: 'manufacturing', industry_sector: 'Auto Components' },
    financial: { employee_count: 120, annual_turnover: 185000000, currency: 'INR' },
    documents: [
      { type: 'PAN_CARD', file_name: 'nova-pan.pdf', verified: true },
      { type: 'GST_CERTIFICATE', file_name: 'nova-gst.pdf', verified: false },
    ],
  },
};

// Scenario B — a legacy vendor: only the few old textual fields exist; everything the new form now
// requires (GSTIN-driven fields, MSME/Udyam, bank verification, SPOC details) is genuinely blank.
const LEGACY_VENDOR = {
  scenario: 'legacy',
  label: 'Legacy vendor — completing new requirements',
  flaggedFields: [],
  original_data: {
    company_info: {
      company_name: 'Sharma Traders',
      business_vertical: 'amber-enterprises',
      contact_person_name: 'Mahesh Sharma',
      // Everything else the new form adds is intentionally absent.
    },
    addresses: {
      registered_address: '18, Gandhi Market',
      registered_city: 'Nagpur',
      registered_country: 'IN',
      // No state / pincode / supply address on file for this old record.
    },
    // No bank_details, msme, compliance, classification, financial or documents on file.
  },
};

const SCENARIOS = { recent: RECENT_VENDOR, legacy: LEGACY_VENDOR };

/** Returns the demo scenario for a `?demo=` value, or null if the id is unknown. */
export function getDemoScenario(id) {
  return SCENARIOS[id] || null;
}

/** Query-param links the Campaigning Tool demo section points at (opened as Utility 2). */
export const DEMO_SCENARIO_LINKS = [
  {
    id: 'recent',
    label: RECENT_VENDOR.label,
    description: 'Details already on file; a few fields flagged to double-check.',
    href: '/public-vendor-registration-form?demo=recent',
  },
  {
    id: 'legacy',
    label: LEGACY_VENDOR.label,
    description: 'Mostly blank; the new requirements need to be completed.',
    href: '/public-vendor-registration-form?demo=legacy',
  },
];
